/**
 * AIFieldSuggestions
 * --------------------------------------------------------------------------
 * Compact "AI suggestions" strip shown under the title/description inputs
 * on incident/wildlife forms. Debounces on typing, calls GPT-5.2 for
 * suggested ADREP category + severity + likelihood, and lets the user
 * one-click apply each.
 *
 * Props:
 *   - title (string)
 *   - description (string)
 *   - onApply({ occurrence_category?, severity?, likelihood?, contributing_factors? })
 *   - token (auth JWT)
 *   - minChars (default 25) — don't fire below this combined length
 *   - hidden (bool) — allow parent to hide (e.g. when form is disabled)
 */
import React, { useEffect, useState, useRef } from 'react';
import { Sparkles, Loader2, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const DEBOUNCE_MS = 900;

export default function AIFieldSuggestions({ title = '', description = '', onApply, token, minChars = 25, hidden = false, kind = 'incident' }) {
  const [suggestion, setSuggestion] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [applied, setApplied] = useState({});
  const abortRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (hidden) return undefined;
    const combined = `${title}\n${description}`.trim();
    if (combined.length < minChars) {
      setSuggestion(null);
      setError(null);
      return undefined;
    }

    // Debounce + cancel in-flight fetch on new keystroke
    if (timerRef.current) clearTimeout(timerRef.current);
    if (abortRef.current) abortRef.current.abort();

    timerRef.current = setTimeout(async () => {
      const ac = new AbortController();
      abortRef.current = ac;
      setLoading(true);
      setError(null);
      try {
        const r = await fetch(`${API_BASE}/api/voice-incident/suggest-fields`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ title, description, kind }),
          signal: ac.signal,
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        setSuggestion(j);
        setApplied({}); // reset — new suggestion means a fresh apply-round
      } catch (e) {
        if (e.name !== 'AbortError') setError(e.message);
      } finally {
        setLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [title, description, token, minChars, hidden, kind]);

  useEffect(() => () => {
    if (abortRef.current) abortRef.current.abort();
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  if (hidden) return null;

  const combined = `${title}\n${description}`.trim();
  if (combined.length < minChars && !loading) {
    return (
      <div className="text-[11px] text-slate-500 italic flex items-center gap-1" data-testid="ai-suggest-hint">
        <Sparkles className="w-3 h-3 text-violet-400/60" />
        Type a bit more and I&apos;ll suggest a category and severity.
      </div>
    );
  }

  if (loading && !suggestion) {
    return (
      <div className="text-[11px] text-violet-300 flex items-center gap-1.5" data-testid="ai-suggest-loading">
        <Loader2 className="w-3 h-3 animate-spin" /> AI is looking at your text…
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-[11px] text-rose-400" data-testid="ai-suggest-error">
        AI suggestion failed: {error}
      </div>
    );
  }

  if (!suggestion) return null;

  const has = (k) => !!suggestion[k];

  const apply = (patch, key) => {
    onApply(patch);
    setApplied((prev) => ({ ...prev, [key]: true }));
  };

  return (
    <div
      className="mt-2 rounded-md border border-violet-500/30 bg-violet-500/5 p-2.5"
      data-testid="ai-suggest-panel"
    >
      <div className="flex items-center gap-2 mb-1.5">
        <Sparkles className="w-3.5 h-3.5 text-violet-300" />
        <span className="text-[11px] uppercase tracking-wider text-violet-200 font-semibold">
          AI suggestions
        </span>
        {suggestion.confidence != null && (
          <Badge className="bg-violet-500/20 text-violet-200 border border-violet-500/40 text-[10px]">
            {Math.round(suggestion.confidence * 100)}%
          </Badge>
        )}
        {loading && <Loader2 className="w-3 h-3 animate-spin text-violet-300 ml-auto" />}
      </div>
      {suggestion.rationale && (
        <div className="text-[11px] text-slate-400 italic mb-2 leading-relaxed">
          {suggestion.rationale}
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        {kind === 'wildlife' ? (
          <>
            {has('species_name') && (
              <SuggestChip
                testId="ai-suggest-species"
                label="Species"
                value={suggestion.species_name}
                applied={applied.species_name}
                onClick={() => apply({ species_name: suggestion.species_name }, 'species_name')}
              />
            )}
            {has('wildlife_category') && (
              <SuggestChip
                testId="ai-suggest-wildlife-category"
                label="Category"
                value={suggestion.wildlife_category}
                applied={applied.wildlife_category}
                onClick={() => apply({ wildlife_category: suggestion.wildlife_category }, 'wildlife_category')}
              />
            )}
            {has('size') && (
              <SuggestChip
                testId="ai-suggest-size"
                label="Size"
                value={suggestion.size}
                applied={applied.size}
                onClick={() => apply({ size: suggestion.size }, 'size')}
              />
            )}
            {has('phase_of_flight') && (
              <SuggestChip
                testId="ai-suggest-phase"
                label="Phase"
                value={suggestion.phase_of_flight}
                applied={applied.phase_of_flight}
                onClick={() => apply({ phase_of_flight: suggestion.phase_of_flight }, 'phase_of_flight')}
              />
            )}
            {has('damage_level') && (
              <SuggestChip
                testId="ai-suggest-damage"
                label="Damage"
                value={suggestion.damage_level}
                applied={applied.damage_level}
                onClick={() => apply({ damage_level: suggestion.damage_level }, 'damage_level')}
              />
            )}
            {has('effect_on_flight') && (
              <SuggestChip
                testId="ai-suggest-effect"
                label="Effect"
                value={suggestion.effect_on_flight.replace(/_/g, ' ')}
                applied={applied.effect_on_flight}
                onClick={() => apply({ effect_on_flight: suggestion.effect_on_flight }, 'effect_on_flight')}
              />
            )}
          </>
        ) : kind === 'risk' ? (
          <>
            {has('risk_category') && (
              <SuggestChip
                testId="ai-suggest-risk-category"
                label="Category"
                value={suggestion.risk_category}
                applied={applied.risk_category}
                onClick={() => apply({ risk_category: suggestion.risk_category }, 'risk_category')}
              />
            )}
            {has('severity') && (
              <SuggestChip
                testId="ai-suggest-severity"
                label="Severity"
                value={suggestion.severity}
                applied={applied.severity}
                onClick={() => apply({ severity: suggestion.severity }, 'severity')}
              />
            )}
            {has('likelihood') && (
              <SuggestChip
                testId="ai-suggest-likelihood"
                label="Likelihood"
                value={suggestion.likelihood.replace(/_/g, ' ')}
                applied={applied.likelihood}
                onClick={() => apply({ likelihood: suggestion.likelihood }, 'likelihood')}
              />
            )}
          </>
        ) : kind === 'fatigue' ? (
          <>
            {has('report_type') && (
              <SuggestChip
                testId="ai-suggest-report-type"
                label="Type"
                value={suggestion.report_type.replace(/_/g, ' ')}
                applied={applied.report_type}
                onClick={() => apply({ report_type: suggestion.report_type }, 'report_type')}
              />
            )}
            {has('hours_awake') && (
              <SuggestChip
                testId="ai-suggest-hours-awake"
                label="Awake"
                value={`${suggestion.hours_awake}h`}
                applied={applied.hours_awake}
                onClick={() => apply({ hours_awake: suggestion.hours_awake }, 'hours_awake')}
              />
            )}
            {has('hours_on_duty') && (
              <SuggestChip
                testId="ai-suggest-hours-on-duty"
                label="On duty"
                value={`${suggestion.hours_on_duty}h`}
                applied={applied.hours_on_duty}
                onClick={() => apply({ hours_on_duty: suggestion.hours_on_duty }, 'hours_on_duty')}
              />
            )}
            {has('sleep_in_last_24h') && (
              <SuggestChip
                testId="ai-suggest-sleep"
                label="Sleep 24h"
                value={`${suggestion.sleep_in_last_24h}h`}
                applied={applied.sleep_in_last_24h}
                onClick={() => apply({ sleep_in_last_24h: suggestion.sleep_in_last_24h }, 'sleep_in_last_24h')}
              />
            )}
            {has('safety_impact') && (
              <SuggestChip
                testId="ai-suggest-safety-impact"
                label="Impact"
                value={suggestion.safety_impact}
                applied={applied.safety_impact}
                onClick={() => apply({ safety_impact: suggestion.safety_impact }, 'safety_impact')}
              />
            )}
          </>
        ) : (
          <>
            {has('occurrence_category') && (
              <SuggestChip
                testId="ai-suggest-category"
                label="Category"
                value={suggestion.occurrence_category}
                applied={applied.category}
                onClick={() => apply({ occurrence_category: suggestion.occurrence_category }, 'category')}
              />
            )}
            {has('severity') && (
              <SuggestChip
                testId="ai-suggest-severity"
                label="Severity"
                value={suggestion.severity}
                applied={applied.severity}
                onClick={() => apply({ severity: suggestion.severity }, 'severity')}
              />
            )}
            {has('likelihood') && (
              <SuggestChip
                testId="ai-suggest-likelihood"
                label="Likelihood"
                value={suggestion.likelihood.replace(/_/g, ' ')}
                applied={applied.likelihood}
                onClick={() => apply({ likelihood: suggestion.likelihood }, 'likelihood')}
              />
            )}
          </>
        )}
        {suggestion.contributing_factors && suggestion.contributing_factors.length > 0 && (
          <SuggestChip
            testId="ai-suggest-factors"
            label="Factors"
            value={`${suggestion.contributing_factors.length} suggested`}
            applied={applied.factors}
            onClick={() => apply({ contributing_factors: suggestion.contributing_factors }, 'factors')}
          />
        )}
      </div>
    </div>
  );
}

const SuggestChip = ({ label, value, onClick, applied, testId }) => (
  <Button
    type="button"
    variant="ghost"
    size="sm"
    onClick={onClick}
    disabled={applied}
    data-testid={testId}
    className={`h-7 px-2.5 border text-[11px] ${
      applied
        ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-200 cursor-default'
        : 'border-violet-500/40 bg-violet-500/10 text-violet-100 hover:bg-violet-500/20'
    }`}
  >
    {applied ? <Check className="w-3 h-3 mr-1" /> : null}
    <span className="text-slate-400 mr-1">{label}:</span>
    <span className="font-mono">{value}</span>
  </Button>
);
